import '.br{position:absolute;}.ssr-plugin'
import '.kg{right:0.25rem;}.ssr-plugin'
import '.kh{bottom:0.25rem;}.ssr-plugin'
import '.d2{z-index:2;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.q{flex-direction:row;}.ssr-plugin'
import '.b8{flex-wrap:wrap;}.ssr-plugin'
import '.r{align-items:center;}.ssr-plugin'
import '.bk{justify-content:flex-end;}.ssr-plugin'
import '.ki{gap:0.125rem;}.ssr-plugin'
import '.as{max-width:100%;}.ssr-plugin'
import '.kj{pointer-events:auto;}.ssr-plugin'
import '.ag{position:relative;}.ssr-plugin'
import '.c7{display:inline-flex;}.ssr-plugin'
import '.bu{right:0px;}.ssr-plugin'
import '.kk{bottom:calc(100% + 0.25rem);}.ssr-plugin'
import '.hf{gap:0.25rem;}.ssr-plugin'
import '.kl{padding:0.375rem;}.ssr-plugin'
import '.f0{border-radius:0.5rem;}.ssr-plugin'
import '.h0{border:1px solid var(--gray-200);}.ssr-plugin'
import '.e3{background:var(--palette-white);}.ssr-plugin'
import '.km{box-shadow:0 2px 8px rgba(0, 0, 0, 0.12);}.ssr-plugin'
import '.kn{z-index:3;}.ssr-plugin'
import '.ko{min-width:14rem;}.ssr-plugin'
import '.ap{flex:1;}.ssr-plugin'
import '.ad{min-width:0px;}.ssr-plugin'
import '.kp{border-radius:0.375rem;}.ssr-plugin'
import '.fb{padding:0.25rem 0.5rem;}.ssr-plugin'
import '.aq{font-size:0.875rem;}.ssr-plugin'
import '.kq{font-family:inherit;}.ssr-plugin'
import '.cn{outline:none;}.ssr-plugin'
import '.dy{color:var(--brand-primary);}.ssr-plugin'
import '.dg{font-size:0.75rem;}.ssr-plugin'
import '.kr{white-space:nowrap;}.ssr-plugin'
import '.ks{color:var(--gray-900);}.ssr-plugin'
import '.kt{color:var(--status-error);}.ssr-plugin'
import { useTranslations } from '@i18n/useLanguage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { $insertList, $removeList, $isListNode } from '@lexical/list'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  faBold,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faStrikethrough,
} from '@fortawesome/pro-regular-svg-icons'
import {
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $getSelection,
  $isRangeSelection,
  type TextFormatType,
} from 'lexical'
import { mergeRegister } from '@lexical/utils'
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { type Style, styled } from 'inlines'

export type ToolbarFormatState = {
  bold: boolean
  italic: boolean
  strikethrough: boolean
  bulletList: boolean
  numberList: boolean
  isLink: boolean
}

const defaultFormatState: ToolbarFormatState = {
  bold: false,
  italic: false,
  strikethrough: false,
  bulletList: false,
  numberList: false,
  isLink: false,
}

const readToolbarFormatState = (): ToolbarFormatState => {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return defaultFormatState
  }

  let bulletList = false
  let numberList = false
  let isLink = false
  let node = selection.anchor.getNode()
  while (node) {
    if ($isListNode(node)) {
      const listType = node.getListType()
      bulletList = listType === 'bullet'
      numberList = listType === 'number'
    }
    if ($isLinkNode(node)) {
      isLink = true
    }
    const parent = node.getParent()
    if (!parent) {
      break
    }
    node = parent
  }

  return {
    bold: selection.hasFormat('bold'),
    italic: selection.hasFormat('italic'),
    strikethrough: selection.hasFormat('strikethrough'),
    bulletList,
    numberList,
    isLink,
  }
}

const readLinkUrl = (): string => {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return ''
  }

  let node = selection.anchor.getNode()
  while (node) {
    if ($isLinkNode(node)) {
      return node.getURL()
    }
    const parent = node.getParent()
    if (!parent) {
      break
    }
    node = parent
  }

  return ''
}

const sanitizeLinkUrl = (url: string): string | null => {
  const trimmed = url.trim()
  if (!trimmed) {
    return null
  }
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/')
  ) {
    return trimmed
  }
  return null
}

const iconToolbarButtonBase: Style = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: '0.35rem',
  borderRadius: '0.375rem',
  color: 'var(--gray-900)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  '&:hover': {
    background: 'var(--gray-100)',
  },
  '&:disabled': {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
}

type LexicalRichHtmlInputToolbarProps = {
  enableLinks?: boolean
}

export const LexicalRichHtmlInputToolbar = ({
  enableLinks,
}: LexicalRichHtmlInputToolbarProps) => {
  const [editor] = useLexicalComposerContext()
  const t = useTranslations().app.dashboard.pageContentEditor
  const [formats, setFormats] = useState<ToolbarFormatState>(defaultFormatState)
  const [showLinkInput, setShowLinkInput] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const linkInputRef = useRef<HTMLInputElement>(null)

  const refreshFormats = useCallback(() => {
    editor.getEditorState().read(() => {
      setFormats(readToolbarFormatState())
    })
  }, [editor])

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          refreshFormats()
          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerUpdateListener(() => {
        refreshFormats()
      }),
    )
  }, [editor, refreshFormats])

  useEffect(() => {
    if (!showLinkInput) {
      return
    }
    linkInputRef.current?.focus()
    linkInputRef.current?.select()
  }, [showLinkInput])

  const dispatchFormat = useCallback(
    (format: TextFormatType) => {
      void editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
    },
    [editor],
  )

  const closeLinkInput = useCallback(() => {
    setShowLinkInput(false)
    setLinkUrl('')
  }, [])

  const openLinkInput = useCallback(() => {
    editor.getEditorState().read(() => {
      setLinkUrl(readLinkUrl())
    })
    setShowLinkInput(true)
  }, [editor])

  const confirmLink = useCallback(() => {
    const sanitized = sanitizeLinkUrl(linkUrl)
    if (!sanitized) {
      return
    }
    void editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitized)
    closeLinkInput()
  }, [editor, linkUrl, closeLinkInput])

  const removeLink = useCallback(() => {
    void editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    closeLinkInput()
  }, [editor, closeLinkInput])

  const handleLinkButtonClick = useCallback(() => {
    openLinkInput()
  }, [openLinkInput])

  const handleLinkInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        confirmLink()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        closeLinkInput()
      }
    },
    [confirmLink, closeLinkInput],
  )

  const activeTint: Style = {
    color: 'var(--brand-primary)',
    background: 'var(--palette-blue-50)',
  }

  return (
    <div className="br kg kh d2 d q b8 r bk ki as kj"
      onMouseDown={(e: MouseEvent) => {
        e.preventDefault()
      }}
    >
      <styled.button
        type="button"
        aria-label={t.richTextBold}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.bold ? activeTint : {}),
        }}
        onClick={() => dispatchFormat('bold')}
      >
        <FontAwesomeIcon
          icon={faBold}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextItalic}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.italic ? activeTint : {}),
        }}
        onClick={() => dispatchFormat('italic')}
      >
        <FontAwesomeIcon
          icon={faItalic}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextStrikethrough}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.strikethrough ? activeTint : {}),
        }}
        onClick={() => dispatchFormat('strikethrough')}
      >
        <FontAwesomeIcon
          icon={faStrikethrough}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextBulletList}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.bulletList ? activeTint : {}),
        }}
        onClick={() => {
          editor.update(() => {
            const f = readToolbarFormatState()
            if (f.bulletList) {
              $removeList()
              return
            }
            if (f.numberList) {
              $removeList()
            }
            $insertList('bullet')
          })
        }}
      >
        <FontAwesomeIcon
          icon={faListUl}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextNumberedList}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.numberList ? activeTint : {}),
        }}
        onClick={() => {
          editor.update(() => {
            const f = readToolbarFormatState()
            if (f.numberList) {
              $removeList()
              return
            }
            if (f.bulletList) {
              $removeList()
            }
            $insertList('number')
          })
        }}
      >
        <FontAwesomeIcon
          icon={faListOl}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      {enableLinks ? (
        <div className="ag c7"
        >
          <styled.button
            type="button"
            aria-label={t.richTextLink}
            style={{
              ...iconToolbarButtonBase,
              ...(formats.isLink ? activeTint : {}),
            }}
            onClick={handleLinkButtonClick}
          >
            <FontAwesomeIcon
              icon={faLink}
              style={{ width: '1rem', height: '1rem' }}
            />
          </styled.button>
          {showLinkInput ? (
            <div className="br bu kk d q r hf kl f0 h0 e3 km kn ko"
            >
              <styled.input className="ap ad h0 kp fb aq kq cn"
                ref={linkInputRef}
                type="url"
                value={linkUrl}
                placeholder={t.richTextLinkUrlPlaceholder}
                aria-label={t.richTextLinkUrlPlaceholder}
                style={{
                  '&:focus': {
                    borderColor: 'var(--palette-blue-300)',
                  },
                }}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={handleLinkInputKeyDown}
              />
              <styled.button className="dy dg fb kr"
                type="button"
                aria-label={t.richTextLinkConfirm}
                style={{
                  ...iconToolbarButtonBase,}}
                onClick={confirmLink}
                disabled={!sanitizeLinkUrl(linkUrl)}
              >
                {t.richTextLinkConfirm}
              </styled.button>
              <styled.button className="ks dg fb kr"
                type="button"
                aria-label={t.richTextLinkCancel}
                style={{
                  ...iconToolbarButtonBase,}}
                onClick={closeLinkInput}
              >
                {t.richTextLinkCancel}
              </styled.button>
              {formats.isLink ? (
                <styled.button className="kt dg fb kr"
                  type="button"
                  aria-label={t.richTextLinkRemove}
                  style={{
                    ...iconToolbarButtonBase,}}
                  onClick={removeLink}
                >
                  {t.richTextLinkRemove}
                </styled.button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
